<template>
	<div class="cuadro-mando-sga">
		<header class="cms-header">
			<div>
				<h2>Cuadro de mando SGA</h2>
				<p class="cms-subtitle">Indicadores de almacén, picking, ubicaciones y productividad</p>
			</div>
			<div class="cms-mes-nav">
				<el-button size="small" icon="el-icon-arrow-left" @click="cambiarMes(-1)" />
				<span class="cms-mes-label">{{ etiquetaMes }}</span>
				<el-button size="small" icon="el-icon-arrow-right" :disabled="esMesActual" @click="cambiarMes(1)" />
				<el-button size="small" @click="irMesActual" :disabled="esMesActual">Mes actual</el-button>
				<el-button size="small" type="primary" icon="el-icon-refresh" :loading="headerLoading" @click="fetchDashboard">Actualizar</el-button>
			</div>
		</header>

		<div v-if="loading && !dataResponse" class="cms-card cms-center" v-loading="true" element-loading-text="Cargando cuadro de mando..." />
		<el-alert v-else-if="error" type="error" :closable="false" :title="error" class="cms-alert" />

		<template v-else-if="dataResponse">
			<div class="cms-kpis">
				<div v-for="item in kpiCards" :key="item.key" class="cms-card cms-kpi">
					<div class="cms-kpi-label"><span class="cms-kpi-icon">{{ item.icon }}</span> {{ item.label }}</div>
					<div class="cms-kpi-value">{{ item.value }}</div>
				</div>
			</div>

			<div class="cms-card">
				<el-tabs v-model="activeTab">
					<el-tab-pane label="Movimientos y picking" name="movimientos">
						<div class="cms-charts-grid">
							<div class="cms-chart-block">
								<h4>Bultos recepcionados y enviados por día</h4>
								<apexchart type="bar" height="280" :options="chartBultos.options" :series="chartBultos.series" />
							</div>
							<div class="cms-chart-block">
								<h4>Unidades pedidas vs servidas en picking</h4>
								<apexchart type="line" height="280" :options="chartPicking.options" :series="chartPicking.series" />
							</div>
						</div>
					</el-tab-pane>

					<el-tab-pane label="Ubicaciones" name="ubicaciones">
						<div class="cms-ubicaciones-kpis">
							<div v-for="item in ubicacionesKpiCards" :key="item.key" class="cms-card cms-kpi">
								<div class="cms-kpi-label"><span class="cms-kpi-icon">{{ item.icon }}</span> {{ item.label }}</div>
								<div class="cms-kpi-value">{{ item.value }}</div>
							</div>
						</div>
						<div class="cms-chart-block cms-chart-block--mt">
							<h4>Ocupación RAC, picking y bultos en suelo por día</h4>
							<apexchart type="line" height="320" :options="chartUbicaciones.options" :series="chartUbicaciones.series" />
						</div>
					</el-tab-pane>

					<el-tab-pane label="Stock inactivo y caducidad" name="stock">
						<div class="cms-buckets-grid">
							<div class="cms-card cms-bucket-card">
								<h4>Bultos sin movimiento</h4>
								<div class="cms-buckets">
									<div v-for="d in diasBuckets" :key="'mov-' + d" class="cms-bucket">
										<span>{{ d }} días</span>
										<strong>{{ numero(bultosSinMovimiento[d]) }}</strong>
									</div>
								</div>
							</div>
							<div class="cms-card cms-bucket-card">
								<h4>Bultos con caducidad próxima</h4>
								<div class="cms-buckets">
									<div v-for="d in diasBuckets" :key="'cad-' + d" class="cms-bucket">
										<span>≤ {{ d }} días</span>
										<strong>{{ numero(bultosCaducidad[d]) }}</strong>
									</div>
								</div>
							</div>
						</div>
						<div class="cms-charts-grid cms-charts-grid--single">
							<div class="cms-chart-block">
								<h4>Comparativa sin movimiento / caducidad</h4>
								<apexchart type="bar" height="260" :options="chartBuckets.options" :series="chartBuckets.series" />
							</div>
						</div>
					</el-tab-pane>

					<el-tab-pane label="Operarios" name="operarios">
						<div class="cms-toolbar">
							<span>Medias de preparación (tiempo real):</span>
							<el-radio-group v-model="operariosVista" size="small">
								<el-radio-button label="dia">Diaria</el-radio-button>
								<el-radio-button label="semana">Semanal</el-radio-button>
							</el-radio-group>
						</div>
						<el-table
							:key="'operarios-' + operariosVista"
							:data="operariosList"
							:default-sort="operariosDefaultSort"
							size="small"
							stripe
							class="cms-table"
							:max-height="420"
						>
							<el-table-column prop="nombre" label="Operario" min-width="160" sortable />
							<el-table-column
								:prop="operariosVista === 'dia' ? 'lineas_dia' : 'lineas_semana'"
								:label="operariosVista === 'dia' ? 'Líneas / día (media)' : 'Líneas / semana (media)'"
								min-width="150"
								align="right"
								sortable
							>
								<template slot-scope="{ row }">{{ numeroDecimal(operariosVista === 'dia' ? row.lineas_dia : row.lineas_semana) }}</template>
							</el-table-column>
							<el-table-column
								:prop="operariosVista === 'dia' ? 'unidades_dia' : 'unidades_semana'"
								:label="operariosVista === 'dia' ? 'Unidades / día (media)' : 'Unidades / semana (media)'"
								min-width="160"
								align="right"
								sortable
							>
								<template slot-scope="{ row }">{{ numeroDecimal(operariosVista === 'dia' ? row.unidades_dia : row.unidades_semana) }}</template>
							</el-table-column>
						</el-table>
						<div class="cms-chart-block cms-chart-block--mt">
							<h4>Productividad por operario ({{ operariosVista === 'dia' ? 'media diaria' : 'media semanal' }})</h4>
							<apexchart type="bar" height="300" :options="chartOperarios.options" :series="chartOperarios.series" />
						</div>
					</el-tab-pane>

					<el-tab-pane label="Pedidos incompletos" name="pedidos" lazy>
						<div class="cms-pedidos-filtros">
							<div class="cms-pedidos-filtros-row">
								<span class="cms-pedidos-label">Agrupación:</span>
								<el-radio-group v-model="pedidosAgrupacion" size="small" @change="onPedidosAgrupacionChange">
									<el-radio-button label="semana">Semana</el-radio-button>
									<el-radio-button label="mes">Mes</el-radio-button>
									<el-radio-button label="anio">Año</el-radio-button>
									<el-radio-button label="historico">Histórico total</el-radio-button>
								</el-radio-group>
							</div>
							<div v-if="pedidosAgrupacion !== 'historico'" class="cms-pedidos-filtros-row">
								<el-form :inline="true" size="small" class="cms-pedidos-form">
									<el-form-item label="Año">
										<el-input-number
											v-model="pedidosAnio"
											:min="2000"
											:max="9999"
											:precision="0"
											controls-position="right"
											style="width: 120px;"
										/>
									</el-form-item>
									<el-form-item v-if="pedidosAgrupacion === 'semana' || pedidosAgrupacion === 'mes'" label="Mes">
										<el-select v-model="pedidosMes" clearable placeholder="Todos" style="width: 120px;">
											<el-option v-for="m in 12" :key="m" :label="nombreMes(m)" :value="m" />
										</el-select>
									</el-form-item>
									<el-form-item v-if="pedidosAgrupacion === 'semana'" label="Semana ISO">
										<el-input-number
											v-model="pedidosSemana"
											:min="1"
											:max="53"
											:precision="0"
											controls-position="right"
											placeholder="Todas"
											style="width: 120px;"
										/>
									</el-form-item>
								</el-form>
							</div>
							<div class="cms-pedidos-filtros-row">
								<el-button type="primary" size="small" :loading="pedidosLoading" @click="fetchPedidosIncompletos">
									Actualizar
								</el-button>
							</div>
						</div>

						<el-alert v-if="pedidosError" type="error" :closable="false" :title="pedidosError" class="cms-alert" />

						<div v-loading="pedidosLoading" class="cms-pedidos-body">
							<div v-if="pedidosMeta" class="cms-pedidos-resumen">
								<span>Preparaciones incompletas: <strong>{{ numero(pedidosMeta.total_pedidos_incompletos) }}</strong></span>
								<span>Filas: <strong>{{ numero(pedidosMeta.total_filas) }}</strong></span>
							</div>

							<el-empty v-if="!pedidosLoading && !pedidosError && pedidosItems.length === 0" description="No hay pedidos incompletos para los filtros seleccionados" />

							<template v-else-if="pedidosItems.length">
								<el-table :data="pedidosItems" size="small" stripe class="cms-table" :max-height="420">
									<el-table-column
										prop="periodo"
										:label="periodoColumnLabel"
										width="100"
										sortable
										:sort-method="sortPedidosPeriodo"
									/>
									<el-table-column prop="cliente" label="Cliente" min-width="180" show-overflow-tooltip sortable />
									<el-table-column prop="pedidos" label="Pedidos" width="90" align="right" sortable />
									<el-table-column prop="articulos_pedidos" label="Art. pedidos" min-width="110" align="right" sortable>
										<template slot-scope="{ row }">{{ numero(row.articulos_pedidos) }}</template>
									</el-table-column>
									<el-table-column prop="articulos_servidos" label="Art. servidos" min-width="110" align="right" sortable>
										<template slot-scope="{ row }">{{ numero(row.articulos_servidos) }}</template>
									</el-table-column>
									<el-table-column
										prop="media_cumplimiento_pct"
										label="Cumplimiento"
										min-width="110"
										align="right"
										sortable
									>
										<template slot-scope="{ row }">
											<el-tag size="mini" :type="tagCumplimiento(row.media_cumplimiento_pct)">
												{{ formatPct(row.media_cumplimiento_pct) }}
											</el-tag>
										</template>
									</el-table-column>
								</el-table>
								<div class="cms-chart-block cms-chart-block--mt">
									<h4>{{ tituloChartPedidos }}</h4>
									<apexchart type="bar" height="280" :options="chartPedidos.options" :series="chartPedidos.series" />
								</div>
							</template>
						</div>
					</el-tab-pane>

					<el-tab-pane label="Unidades por cliente" name="unidades-cliente" lazy>
						<el-alert v-if="articulosError" type="error" :closable="false" :title="articulosError" class="cms-alert" />
						<div v-loading="articulosLoading" class="cms-articulos-body">
							<el-empty
								v-if="!articulosLoading && !articulosError && articulosData && unidadesPorCliente.length === 0"
								description="Sin datos en este mes"
							/>
							<template v-else-if="articulosData">
								<p class="cms-articulos-hint">Unidades servidas agrupadas por artículo y cliente.</p>
								<div class="cms-articulos-filtros">
									<el-input
										v-model="articuloFiltroCliente"
										size="small"
										clearable
										placeholder="Filtrar por código o descripción"
										prefix-icon="el-icon-search"
										class="cms-articulos-filtro"
									/>
									<el-input
										v-model="clienteFiltro"
										size="small"
										clearable
										placeholder="Filtrar por código o nombre de cliente"
										prefix-icon="el-icon-search"
										class="cms-articulos-filtro"
									/>
								</div>
								<el-table
									:data="unidadesClientePagina"
									size="small"
									stripe
									class="cms-table"
									:max-height="420"
									:default-sort="{ prop: 'unidades', order: 'descending' }"
								>
									<el-table-column prop="cod_articulo" label="Código art." width="120" sortable />
									<el-table-column prop="descripcion" label="Descripción" min-width="160" show-overflow-tooltip sortable />
									<el-table-column prop="cod_cliente" label="Cód. cliente" width="110" sortable />
									<el-table-column prop="cliente" label="Cliente" min-width="160" show-overflow-tooltip sortable />
									<el-table-column prop="unidades" label="Unidades" width="110" align="right" sortable>
										<template slot-scope="{ row }">{{ numero(row.unidades) }}</template>
									</el-table-column>
								</el-table>
								<el-pagination
									v-if="unidadesClienteFiltradas.length > clientePageSize"
									class="cms-articulos-pagination"
									layout="total, prev, pager, next"
									:current-page.sync="clientePage"
									:page-size="clientePageSize"
									:total="unidadesClienteFiltradas.length"
									small
								/>
							</template>
						</div>
					</el-tab-pane>

					<el-tab-pane label="Artículos" name="articulos" lazy>
						<el-alert v-if="articulosError" type="error" :closable="false" :title="articulosError" class="cms-alert" />
						<div v-loading="articulosLoading" class="cms-articulos-body cms-articulos-body--tall">
							<el-empty
								v-if="!articulosLoading && !articulosError && articulosData && !articulosFilasTieneDatos"
								description="Sin datos en este mes"
							/>
							<div v-else-if="articulosData" class="cms-articulos-triple cms-articulos-triple--tall">
								<section class="cms-articulos-section cms-articulos-section--col">
									<h4>Rotación de artículos</h4>
									<p class="cms-articulos-hint">Preparaciones distintas del mes por artículo.</p>
									<el-empty v-if="rotacionArticulos.length === 0" description="Sin datos" :image-size="48" />
									<el-table
										v-else
										:data="rotacionArticulos"
										size="small"
										stripe
										class="cms-table cms-table--dense"
										:max-height="articulosTablaAltura"
										:default-sort="{ prop: 'veces_en_pedido', order: 'descending' }"
									>
										<el-table-column prop="cod_articulo" label="Cód." width="94" class-name="cms-col-cod" sortable />
										<el-table-column prop="descripcion" label="Descripción" min-width="80" show-overflow-tooltip sortable />
										<el-table-column prop="veces_en_pedido" label="Veces" width="68" align="right" sortable>
											<template slot-scope="{ row }">{{ numero(row.veces_en_pedido) }}</template>
										</el-table-column>
									</el-table>
								</section>

								<section class="cms-articulos-section cms-articulos-section--col">
									<h4>Top 30 recepciones</h4>
									<p class="cms-articulos-hint">Bultos verificados en entrada.</p>
									<el-empty v-if="topRecepciones.length === 0" description="Sin datos" :image-size="48" />
									<el-table
										v-else
										:data="topRecepciones"
										size="small"
										stripe
										class="cms-table cms-table--dense"
										:max-height="articulosTablaAltura"
										:default-sort="{ prop: 'recepciones', order: 'descending' }"
									>
										<el-table-column type="index" label="#" width="36" />
										<el-table-column prop="cod_articulo" label="Cód." width="94" class-name="cms-col-cod" sortable />
										<el-table-column prop="descripcion" label="Descripción" min-width="80" show-overflow-tooltip sortable />
										<el-table-column prop="recepciones" label="Rec." width="60" align="right" sortable>
											<template slot-scope="{ row }">{{ numero(row.recepciones) }}</template>
										</el-table-column>
									</el-table>
								</section>

								<section class="cms-articulos-section cms-articulos-section--col">
									<h4>Top 30 expediciones</h4>
									<p class="cms-articulos-hint">Unidades servidas en preparaciones.</p>
									<el-empty v-if="topExpediciones.length === 0" description="Sin datos" :image-size="48" />
									<el-table
										v-else
										:data="topExpediciones"
										size="small"
										stripe
										class="cms-table cms-table--dense"
										:max-height="articulosTablaAltura"
										:default-sort="{ prop: 'unidades', order: 'descending' }"
									>
										<el-table-column type="index" label="#" width="36" />
										<el-table-column prop="cod_articulo" label="Cód." width="94" class-name="cms-col-cod" sortable />
										<el-table-column prop="descripcion" label="Descripción" min-width="80" show-overflow-tooltip sortable />
										<el-table-column prop="unidades" label="Uds." width="56" align="right" sortable>
											<template slot-scope="{ row }">{{ numero(row.unidades) }}</template>
										</el-table-column>
										<el-table-column prop="preparaciones" label="Prep." width="56" align="right" sortable>
											<template slot-scope="{ row }">{{ numero(row.preparaciones) }}</template>
										</el-table-column>
									</el-table>
								</section>
							</div>
						</div>
					</el-tab-pane>

					<el-tab-pane label="Movimientos" name="movimientos-articulos" lazy>
						<el-alert v-if="articulosError" type="error" :closable="false" :title="articulosError" class="cms-alert" />
						<div v-loading="articulosLoading" class="cms-articulos-body">
							<el-empty
								v-if="!articulosLoading && !articulosError && articulosData && !movimientosArticulosTieneDatos"
								description="Sin datos en este mes"
							/>
							<div v-else-if="articulosData" class="cms-articulos-double">
								<section class="cms-articulos-section cms-articulos-section--col">
									<h4>Top 25 reposiciones</h4>
									<p class="cms-articulos-hint">Reposición a picking completada en el mes.</p>
									<el-empty v-if="topReposiciones.length === 0" description="Sin datos en este mes" :image-size="64" />
									<div v-else class="cms-chart-block">
										<apexchart type="bar" :height="chartTopReposiciones.height" :options="chartTopReposiciones.options" :series="chartTopReposiciones.series" />
									</div>
								</section>

								<section class="cms-articulos-section cms-articulos-section--col">
									<h4>Top 25 reubicaciones</h4>
									<p class="cms-articulos-hint">Reubicación completada en el mes (vinculada a bulto).</p>
									<el-empty v-if="topReubicaciones.length === 0" description="Sin datos en este mes" :image-size="64" />
									<div v-else class="cms-chart-block">
										<apexchart type="bar" :height="chartTopReubicaciones.height" :options="chartTopReubicaciones.options" :series="chartTopReubicaciones.series" />
									</div>
								</section>
							</div>
						</div>
					</el-tab-pane>
				</el-tabs>
			</div>
		</template>
	</div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import VueApexCharts from 'vue-apexcharts'
import { getUrl } from '@/helpers/api'

moment.locale('es')

const DIAS_BUCKETS = [30, 60, 90, 120]
const TABS_ARTICULOS_API = ['unidades-cliente', 'articulos', 'movimientos-articulos']

export default {
	name: 'CuadroMandoSGA',
	components: {
		apexchart: VueApexCharts
	},
	data() {
		const hoy = moment()
		return {
			loading: false,
			error: '',
			dataResponse: null,
			anio: hoy.year(),
			mes: hoy.month() + 1,
			activeTab: 'movimientos',
			operariosVista: 'dia',
			diasBuckets: DIAS_BUCKETS,
			pedidosAgrupacion: 'semana',
			pedidosAnio: null,
			pedidosMes: null,
			pedidosSemana: null,
			pedidosLoading: false,
			pedidosError: '',
			pedidosData: null,
			articulosLoading: false,
			articulosError: '',
			articulosData: null,
			articulosNeedsReload: false,
			articuloFiltroCliente: '',
			clienteFiltro: '',
			clientePage: 1,
			clientePageSize: 25,
			articulosTablaAltura: 640
		}
	},
	watch: {
		activeTab(val) {
			if (val === 'pedidos') {
				this.prepararFiltrosPedidosPorDefecto()
				this.fetchPedidosIncompletos()
			}
			if (TABS_ARTICULOS_API.includes(val) && (this.articulosNeedsReload || !this.articulosData)) {
				this.fetchArticulos()
			}
		},
		articuloFiltroCliente() {
			this.clientePage = 1
		},
		clienteFiltro() {
			this.clientePage = 1
		}
	},
	computed: {
		etiquetaMes() {
			return moment({ year: this.anio, month: this.mes - 1, day: 1 }).format('MMMM YYYY')
		},
		esMesActual() {
			const hoy = moment()
			return this.anio === hoy.year() && this.mes === hoy.month() + 1
		},
		headerLoading() {
			return this.loading || (TABS_ARTICULOS_API.includes(this.activeTab) && this.articulosLoading)
		},
		kpi() {
			return this.dataResponse && this.dataResponse.kpi ? this.dataResponse.kpi : {}
		},
		kpiCards() {
			const k = this.kpi
			return [
				{ key: 'recep', icon: '📥', label: 'Bultos recepcionados (mes)', value: this.numero(k.bultos_recepcionados_mes) },
				{ key: 'env', icon: '📤', label: 'Bultos enviados (mes)', value: this.numero(k.bultos_enviados_mes) },
				{ key: 'pick', icon: '📦', label: 'Unidades servidas picking', value: this.numero(k.unidades_servidas_mes) },
				{ key: 'inc', icon: '⚠️', label: 'Pedidos incompletos (mes)', value: this.numero(k.pedidos_incompletos_mes) }
			]
		},
		diasMes() {
			return this.buildDiasEtiquetas()
		},
		bultosPorDia() {
			return Array.isArray(this.dataResponse && this.dataResponse.bultos_por_dia) ? this.dataResponse.bultos_por_dia : []
		},
		pickingPorDia() {
			return Array.isArray(this.dataResponse && this.dataResponse.picking_por_dia) ? this.dataResponse.picking_por_dia : []
		},
		ubicacionesRacPorDia() {
			return Array.isArray(this.dataResponse && this.dataResponse.ubicaciones_rac_por_dia) ? this.dataResponse.ubicaciones_rac_por_dia : []
		},
		ubicacionesPickingPorDia() {
			return Array.isArray(this.dataResponse && this.dataResponse.ubicaciones_picking_por_dia) ? this.dataResponse.ubicaciones_picking_por_dia : []
		},
		bultosSueloPorDia() {
			return Array.isArray(this.dataResponse && this.dataResponse.bultos_suelo_por_dia) ? this.dataResponse.bultos_suelo_por_dia : []
		},
		totalUbicacionesRac() {
			return this.calcTotalUbicacionesCapacidad(this.ubicacionesRacPorDia)
		},
		totalUbicacionesPicking() {
			return this.calcTotalUbicacionesCapacidad(this.ubicacionesPickingPorDia)
		},
		maximoBultosSuelo() {
			const valores = this.mapSerieDia(this.bultosSueloPorDia, 'bultos')
			return valores.length ? Math.max(...valores) : 0
		},
		ubicacionesKpiCards() {
			return [
				{ key: 'rac-total', icon: '🏷️', label: 'Total ubicaciones RAC', value: this.numero(this.totalUbicacionesRac) },
				{ key: 'pick-total', icon: '📍', label: 'Total ubicaciones Picking', value: this.numero(this.totalUbicacionesPicking) },
				{ key: 'suelo-max', icon: '📋', label: 'Máximo bultos suelo', value: this.numero(this.maximoBultosSuelo) }
			]
		},
		bultosSinMovimiento() {
			return this.dataResponse && this.dataResponse.bultos_sin_movimiento ? this.dataResponse.bultos_sin_movimiento : {}
		},
		bultosCaducidad() {
			return this.dataResponse && this.dataResponse.bultos_caducidad ? this.dataResponse.bultos_caducidad : {}
		},
		operariosList() {
			return Array.isArray(this.dataResponse && this.dataResponse.operarios) ? this.dataResponse.operarios : []
		},
		operariosDefaultSort() {
			return {
				prop: this.operariosVista === 'dia' ? 'unidades_dia' : 'unidades_semana',
				order: 'descending'
			}
		},
		operariosOrdenados() {
			const campo = this.operariosVista === 'dia' ? 'unidades_dia' : 'unidades_semana'
			return this.operariosList.slice().sort((a, b) => Number(b[campo] || 0) - Number(a[campo] || 0))
		},
		pedidosMeta() {
			return this.pedidosData && this.pedidosData.meta ? this.pedidosData.meta : null
		},
		pedidosItems() {
			return this.pedidosData && Array.isArray(this.pedidosData.items) ? this.pedidosData.items : []
		},
		periodoColumnLabel() {
			const map = {
				semana: 'Semana',
				mes: 'Mes',
				anio: 'Año',
				historico: 'Total'
			}
			return map[this.pedidosAgrupacion] || 'Periodo'
		},
		tituloChartPedidos() {
			const map = {
				semana: 'Artículos pedidos vs servidos por semana',
				mes: 'Artículos pedidos vs servidos por mes',
				anio: 'Artículos pedidos vs servidos por año',
				historico: 'Artículos pedidos vs servidos (histórico total)'
			}
			return map[this.pedidosAgrupacion] || 'Artículos pedidos vs servidos'
		},
		chartBultos() {
			return this.buildBarChart(
				['Recepcionados', 'Enviados'],
				[
					this.mapSerieDia(this.bultosPorDia, 'recepcionados'),
					this.mapSerieDia(this.bultosPorDia, 'enviados')
				]
			)
		},
		chartPicking() {
			return this.buildLineChart(
				['Pedidas', 'Servidas'],
				[
					this.mapSerieDia(this.pickingPorDia, 'pedidas'),
					this.mapSerieDia(this.pickingPorDia, 'servidas')
				]
			)
		},
		chartUbicaciones() {
			const racTotal = this.totalUbicacionesRac
			const pickTotal = this.totalUbicacionesPicking
			const sueloMax = this.maximoBultosSuelo
			const colors = ['#409EFF', '#67C23A', '#E6A23C']
			const base = this.buildLineChart(
				['Ocupación RAC', 'Ocupación picking', 'Bultos en suelo'],
				[
					this.mapSerieDia(this.ubicacionesRacPorDia, 'ocupadas'),
					this.mapSerieDia(this.ubicacionesPickingPorDia, 'ocupadas'),
					this.mapSerieDia(this.bultosSueloPorDia, 'bultos')
				],
				colors
			)
			const annotations = []
			if (racTotal > 0) {
				annotations.push(this.buildUbicacionesAnnotation(racTotal, colors[0], 'Total RAC'))
			}
			if (pickTotal > 0) {
				annotations.push(this.buildUbicacionesAnnotation(pickTotal, colors[1], 'Total picking'))
			}
			if (sueloMax > 0) {
				annotations.push(this.buildUbicacionesAnnotation(sueloMax, colors[2], 'Máx. suelo'))
			}
			base.options.annotations = { yaxis: annotations }
			return base
		},
		chartBuckets() {
			const labels = DIAS_BUCKETS.map(d => `${d}d`)
			return {
				options: {
					chart: { toolbar: { show: false }, stacked: false },
					xaxis: { categories: labels },
					legend: { position: 'top' },
					dataLabels: { enabled: false },
					colors: ['#e6a23c', '#f56c6c']
				},
				series: [
					{ name: 'Sin movimiento', data: DIAS_BUCKETS.map(d => Number(this.bultosSinMovimiento[d] || this.bultosSinMovimiento[String(d)] || 0)) },
					{ name: 'Caducidad próxima', data: DIAS_BUCKETS.map(d => Number(this.bultosCaducidad[d] || this.bultosCaducidad[String(d)] || 0)) }
				]
			}
		},
		chartOperarios() {
			const campoLineas = this.operariosVista === 'dia' ? 'lineas_dia' : 'lineas_semana'
			const campoUnidades = this.operariosVista === 'dia' ? 'unidades_dia' : 'unidades_semana'
			const nombres = this.operariosOrdenados.map(o => o.nombre || '—')
			return this.buildBarChart(
				['Líneas', 'Unidades'],
				[
					this.operariosOrdenados.map(o => Number(o[campoLineas] || 0)),
					this.operariosOrdenados.map(o => Number(o[campoUnidades] || 0))
				],
				false,
				nombres
			)
		},
		chartPedidos() {
			const porPeriodo = {}
			this.pedidosItems.forEach(row => {
				const p = row.periodo || '—'
				if (!porPeriodo[p]) porPeriodo[p] = { pedidos: 0, servidos: 0 }
				porPeriodo[p].pedidos += Number(row.articulos_pedidos || 0)
				porPeriodo[p].servidos += Number(row.articulos_servidos || 0)
			})
			const periodos = Object.keys(porPeriodo).sort(this.ordenarPeriodo)
			return this.buildBarChart(
				['Pedidos', 'Servidos'],
				[
					periodos.map(p => porPeriodo[p].pedidos),
					periodos.map(p => porPeriodo[p].servidos)
				],
				false,
				periodos
			)
		},
		articulosMeta() {
			return this.articulosData && this.articulosData.meta ? this.articulosData.meta : null
		},
		rotacionArticulos() {
			return Array.isArray(this.articulosData && this.articulosData.rotacion_articulos)
				? this.articulosData.rotacion_articulos
				: []
		},
		unidadesPorCliente() {
			return Array.isArray(this.articulosData && this.articulosData.unidades_por_cliente)
				? this.articulosData.unidades_por_cliente
				: []
		},
		unidadesClienteFiltradas() {
			const qArt = this.articuloFiltroCliente.trim().toLowerCase()
			const qCli = this.clienteFiltro.trim().toLowerCase()
			if (!qArt && !qCli) return this.unidadesPorCliente
			return this.unidadesPorCliente.filter(row => {
				if (qArt) {
					const cod = String(row.cod_articulo || '').toLowerCase()
					const desc = String(row.descripcion || '').toLowerCase()
					if (!cod.includes(qArt) && !desc.includes(qArt)) return false
				}
				if (qCli) {
					const codCli = String(row.cod_cliente || '').toLowerCase()
					const nombre = String(row.cliente || '').toLowerCase()
					if (!codCli.includes(qCli) && !nombre.includes(qCli)) return false
				}
				return true
			})
		},
		unidadesClientePagina() {
			const start = (this.clientePage - 1) * this.clientePageSize
			return this.unidadesClienteFiltradas.slice(start, start + this.clientePageSize)
		},
		topRecepciones() {
			return Array.isArray(this.articulosData && this.articulosData.top_recepciones)
				? this.articulosData.top_recepciones
				: []
		},
		topExpediciones() {
			return Array.isArray(this.articulosData && this.articulosData.top_expediciones)
				? this.articulosData.top_expediciones
				: []
		},
		topReposiciones() {
			return Array.isArray(this.articulosData && this.articulosData.top_reposiciones)
				? this.articulosData.top_reposiciones
				: []
		},
		topReubicaciones() {
			return Array.isArray(this.articulosData && this.articulosData.top_reubicaciones)
				? this.articulosData.top_reubicaciones
				: []
		},
		articulosFilasTieneDatos() {
			return (
				this.rotacionArticulos.length > 0 ||
				this.topRecepciones.length > 0 ||
				this.topExpediciones.length > 0
			)
		},
		movimientosArticulosTieneDatos() {
			return this.topReposiciones.length > 0 || this.topReubicaciones.length > 0
		},
		chartTopReposiciones() {
			return this.buildTopHorizontalChart(this.topReposiciones, 'reposiciones', 'Reposiciones', ['#E6A23C'])
		},
		chartTopReubicaciones() {
			return this.buildTopHorizontalChart(this.topReubicaciones, 'reubicaciones', 'Reubicaciones', ['#909399'])
		}
	},
	created() {
		this.fetchDashboard()
	},
	methods: {
		numero(v) {
			return new Intl.NumberFormat('es-ES').format(Number(v || 0))
		},
		numeroDecimal(v, decimales) {
			const max = decimales != null ? decimales : 1
			return new Intl.NumberFormat('es-ES', {
				minimumFractionDigits: 0,
				maximumFractionDigits: max
			}).format(Number(v || 0))
		},
		formatPct(v) {
			const n = Number(v || 0)
			return `${n.toFixed(2)} %`
		},
		tagCumplimiento(pct) {
			const n = Number(pct || 0)
			if (n >= 95) return 'success'
			if (n >= 80) return 'warning'
			return 'danger'
		},
		buildDiasEtiquetas() {
			const total = moment({ year: this.anio, month: this.mes - 1 }).daysInMonth()
			const labels = []
			for (let d = 1; d <= total; d++) {
				labels.push(String(d))
			}
			return labels
		},
		mapSerieDia(rows, campo) {
			const map = {}
			rows.forEach(r => {
				const dia = Number(r.dia)
				if (!isNaN(dia)) map[dia] = Number(r[campo] || 0)
			})
			return this.diasMes.map((_, idx) => map[idx + 1] != null ? map[idx + 1] : 0)
		},
		calcTotalUbicacionesCapacidad(rows) {
			let total = 0
			rows.forEach(r => {
				const cap = Number(r.ocupadas || 0) + Number(r.libres || 0)
				if (cap > total) total = cap
			})
			return total
		},
		buildUbicacionesAnnotation(valor, color, texto) {
			return {
				y: valor,
				borderColor: color,
				strokeDashArray: 5,
				label: {
					borderColor: color,
					style: {
						color: '#fff',
						background: color,
						fontSize: '11px'
					},
					text: `${texto}: ${this.numero(valor)}`,
					position: 'left',
					offsetX: 8
				}
			}
		},
		buildBarChart(seriesNames, seriesData, stacked, categories) {
			const cats = categories || this.diasMes
			return {
				options: {
					chart: { toolbar: { show: false }, stacked: !!stacked },
					plotOptions: { bar: { horizontal: false, columnWidth: stacked ? '65%' : '55%' } },
					xaxis: { categories: cats, labels: { rotate: cats.length > 20 ? -45 : 0 } },
					legend: { position: 'top' },
					dataLabels: { enabled: false },
					colors: ['#409EFF', '#67C23A', '#E6A23C']
				},
				series: seriesNames.map((name, i) => ({ name, data: seriesData[i] || [] }))
			}
		},
		buildLineChart(seriesNames, seriesData, colors) {
			const palette = colors || ['#909399', '#409EFF']
			return {
				options: {
					chart: { toolbar: { show: false } },
					stroke: { curve: 'smooth', width: 2 },
					xaxis: { categories: this.diasMes },
					legend: { position: 'top' },
					dataLabels: { enabled: false },
					colors: palette
				},
				series: seriesNames.map((name, i) => ({ name, data: seriesData[i] || [] }))
			}
		},
		etiquetaArticuloChart(row) {
			const cod = row.cod_articulo ? String(row.cod_articulo) : ''
			const desc = row.descripcion ? String(row.descripcion) : '—'
			const texto = cod ? `${cod} · ${desc}` : desc
			return texto.length > 36 ? `${texto.slice(0, 36)}…` : texto
		},
		buildTopHorizontalChart(rows, valueField, seriesName, colors, seriesDefs) {
			const defs = seriesDefs || [{ field: valueField, name: seriesName }]
			const sortField = defs[0].field
			const list = rows.slice().sort((a, b) => Number(b[sortField] || 0) - Number(a[sortField] || 0))
			const categories = list.map(row => this.etiquetaArticuloChart(row))
			const height = Math.max(220, list.length * 26 + 60)
			return {
				height,
				options: {
					chart: { toolbar: { show: false } },
					plotOptions: { bar: { horizontal: true, barHeight: defs.length > 1 ? '65%' : '70%' } },
					xaxis: { categories },
					legend: { position: 'top' },
					dataLabels: { enabled: false },
					colors: colors || ['#409EFF']
				},
				series: defs.map(def => ({
					name: def.name,
					data: list.map(row => Number(row[def.field] || 0))
				}))
			}
		},
		nombreMes(num) {
			return moment({ month: num - 1, day: 1 }).format('MMMM')
		},
		prepararFiltrosPedidosPorDefecto() {
			if (this.pedidosAgrupacion === 'semana' && !this.pedidosData) {
				this.pedidosAnio = this.anio
				this.pedidosMes = this.mes
				this.pedidosSemana = null
			}
			if (this.pedidosAnio == null) this.pedidosAnio = this.anio
			if (
				this.pedidosMes == null &&
				this.pedidosAgrupacion !== 'anio' &&
				this.pedidosAgrupacion !== 'historico'
			) {
				this.pedidosMes = this.mes
			}
		},
		onPedidosAgrupacionChange() {
			if (this.pedidosAgrupacion === 'historico') {
				this.pedidosSemana = null
			} else if (this.pedidosAgrupacion === 'anio') {
				this.pedidosMes = null
				this.pedidosSemana = null
				if (this.pedidosAnio == null) this.pedidosAnio = this.anio
			} else if (this.pedidosAgrupacion === 'mes') {
				this.pedidosSemana = null
				if (this.pedidosAnio == null) this.pedidosAnio = this.anio
			} else if (this.pedidosAgrupacion === 'semana') {
				if (this.pedidosAnio == null) this.pedidosAnio = this.anio
				if (this.pedidosMes == null) this.pedidosMes = this.mes
			}
			if (this.activeTab === 'pedidos') {
				this.fetchPedidosIncompletos()
			}
		},
		buildPedidosParams() {
			const params = { agrupacion: this.pedidosAgrupacion }
			if (this.pedidosAgrupacion === 'historico') return params
			params.anio = this.pedidosAnio != null ? this.pedidosAnio : this.anio
			if (this.pedidosAgrupacion === 'semana') {
				if (this.pedidosMes != null && this.pedidosMes !== '') params.mes = this.pedidosMes
				if (this.pedidosSemana != null && this.pedidosSemana !== '') params.semana = this.pedidosSemana
			} else if (this.pedidosAgrupacion === 'mes') {
				if (this.pedidosMes != null && this.pedidosMes !== '') params.mes = this.pedidosMes
			}
			return params
		},
		sortPedidosPeriodo(a, b) {
			return this.ordenarPeriodo(a.periodo, b.periodo)
		},
		ordenarPeriodo(a, b) {
			if (a === 'total' && b !== 'total') return 1
			if (b === 'total' && a !== 'total') return -1
			const na = this.parsePeriodoOrden(a)
			const nb = this.parsePeriodoOrden(b)
			if (na !== nb) return na - nb
			return String(a).localeCompare(String(b))
		},
		parsePeriodoOrden(periodo) {
			const s = String(periodo || '')
			if (s === 'total') return 999999
			if (/^S\d+/i.test(s)) return parseInt(s.replace(/\D/g, ''), 10) || 0
			if (/^M\d+/i.test(s)) return parseInt(s.replace(/\D/g, ''), 10) || 0
			if (/^\d{4}$/.test(s)) return parseInt(s, 10) * 100
			return 0
		},
		sincronizarPedidosConCuadroMensual() {
			if (this.activeTab !== 'pedidos') return
			if (this.pedidosAgrupacion === 'semana') {
				this.pedidosAnio = this.anio
				this.pedidosMes = this.mes
				this.fetchPedidosIncompletos()
			}
		},
		sincronizarArticulosConCuadroMensual() {
			if (TABS_ARTICULOS_API.includes(this.activeTab)) {
				this.fetchArticulos()
			} else if (this.articulosData) {
				this.articulosNeedsReload = true
			}
		},
		async fetchArticulos() {
			this.articulosLoading = true
			this.articulosError = ''
			try {
				const endpoints = getUrl()
				const base = endpoints && endpoints.backend ? endpoints.backend : '/'
				const url = `${base}dashboard/articulosCuadroMandoSga`
				const { data } = await axios.get(url, { params: { anio: this.anio, mes: this.mes } })
				if (data && data.success === false) {
					throw Object.assign(new Error(data.message || 'Error al cargar artículos'), { response: { data } })
				}
				const payload = data && data.data ? data.data : data
				if (!payload || typeof payload !== 'object') {
					throw new Error('Respuesta del servidor no válida')
				}
				this.articulosData = payload
				this.articulosNeedsReload = false
				this.articuloFiltroCliente = ''
				this.clienteFiltro = ''
				this.clientePage = 1
			} catch (err) {
				console.error('CuadroMandoSGA articulos:', err)
				const msg = err.response && err.response.data && err.response.data.message
					? err.response.data.message
					: (err.message || `Error HTTP (${(err.response && err.response.status) || 'sin código'})`)
				this.articulosError = msg
				this.articulosData = null
			} finally {
				this.articulosLoading = false
			}
		},
		async fetchPedidosIncompletos() {
			this.pedidosLoading = true
			this.pedidosError = ''
			try {
				const endpoints = getUrl()
				const base = endpoints && endpoints.backend ? endpoints.backend : '/'
				const url = `${base}dashboard/pedidosIncompletosSga`
				const { data } = await axios.get(url, { params: this.buildPedidosParams() })
				if (data && data.success === false) {
					throw Object.assign(new Error(data.message || 'Error al cargar pedidos incompletos'), { response: { data } })
				}
				const payload = data && data.data ? data.data : data
				if (!payload || typeof payload !== 'object') {
					throw new Error('Respuesta del servidor no válida')
				}
				this.pedidosData = payload
			} catch (err) {
				console.error('CuadroMandoSGA pedidos:', err)
				const msg = err.response && err.response.data && err.response.data.message
					? err.response.data.message
					: (err.message || `Error HTTP (${(err.response && err.response.status) || 'sin código'})`)
				this.pedidosError = msg
				this.pedidosData = null
			} finally {
				this.pedidosLoading = false
			}
		},
		cambiarMes(delta) {
			let m = this.mes + delta
			let y = this.anio
			if (m < 1) { m = 12; y -= 1 }
			if (m > 12) { m = 1; y += 1 }
			const hoy = moment()
			if (y > hoy.year() || (y === hoy.year() && m > hoy.month() + 1)) return
			this.mes = m
			this.anio = y
			this.fetchDashboard()
			this.sincronizarPedidosConCuadroMensual()
			this.sincronizarArticulosConCuadroMensual()
		},
		irMesActual() {
			const hoy = moment()
			this.anio = hoy.year()
			this.mes = hoy.month() + 1
			this.fetchDashboard()
			this.sincronizarPedidosConCuadroMensual()
			this.sincronizarArticulosConCuadroMensual()
		},
		async fetchDashboard() {
			this.loading = true
			this.error = ''
			try {
				const endpoints = getUrl()
				const base = endpoints && endpoints.backend ? endpoints.backend : '/'
				const url = `${base}dashboard/cuadroMandoSga`
				const { data } = await axios.get(url, { params: { anio: this.anio, mes: this.mes } })
				if (data && data.success === false) {
					throw Object.assign(new Error(data.message || 'Error al cargar el cuadro de mando'), { response: { data } })
				}
				const payload = data && data.data ? data.data : data
				if (!payload || typeof payload !== 'object') {
					throw new Error('Respuesta del servidor no válida')
				}
				this.dataResponse = payload
			} catch (err) {
				console.error('CuadroMandoSGA:', err)
				const msg = err.response && err.response.data && err.response.data.message
					? err.response.data.message
					: (err.message || `Error HTTP al cargar el panel (${(err.response && err.response.status) || 'sin código'})`)
				this.error = msg
				this.dataResponse = null
			} finally {
				this.loading = false
			}
			if (TABS_ARTICULOS_API.includes(this.activeTab) && this.dataResponse) {
				this.fetchArticulos()
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.cuadro-mando-sga {
	padding: 16px;
	min-height: 100%;
	background: $--color-background;
}

.cms-header {
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	align-items: flex-start;
	gap: 12px;
	margin-bottom: 12px;

	h2 {
		margin: 0 0 4px;
		color: $--color-primary;
	}

	.cms-subtitle {
		margin: 0;
		font-size: 0.9rem;
		color: #909399;
	}
}

.cms-mes-nav {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.cms-mes-label {
	min-width: 140px;
	text-align: center;
	font-weight: 600;
	text-transform: capitalize;
}

.cms-alert {
	margin-bottom: 12px;
}

.cms-card {
	background: $--color-surface;
	color: $--color-surface-text;
	border: 1px solid #ebeef5;
	border-radius: 6px;
	padding: 12px;
	margin-bottom: 12px;
}

.cms-center {
	min-height: 160px;
}

.cms-kpis {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 10px;
	margin-bottom: 12px;

	.cms-card {
		margin-bottom: 0;
	}
}

.cms-kpi-label {
	font-size: 12px;
	opacity: 0.85;
	display: flex;
	align-items: center;
	gap: 6px;
}

.cms-kpi-icon {
	font-size: 14px;
}

.cms-kpi-value {
	font-size: 22px;
	font-weight: 700;
	margin-top: 4px;
}

.cms-ubicaciones-kpis {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 10px;
	margin-bottom: 12px;

	.cms-card {
		margin-bottom: 0;
	}
}

.cms-charts-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;

	&--single {
		grid-template-columns: 1fr;
		margin-top: 12px;
	}

	&--triple {
		grid-template-columns: repeat(3, 1fr);
	}
}

.cms-chart-block {
	h4 {
		margin: 0 0 8px;
		font-size: 13px;
		font-weight: 600;
	}

	&--mt {
		margin-top: 16px;
	}
}

.cms-buckets-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 12px;
	margin-bottom: 8px;
}

.cms-bucket-card {
	margin-bottom: 0;

	h4 {
		margin: 0 0 10px;
		font-size: 13px;
	}
}

.cms-buckets {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 8px;
}

.cms-bucket {
	border: 1px solid #ebeef5;
	border-radius: 6px;
	padding: 10px;
	text-align: center;

	span {
		display: block;
		font-size: 11px;
		color: #909399;
		margin-bottom: 4px;
	}

	strong {
		font-size: 18px;
	}
}

.cms-toolbar {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 12px;
	font-size: 13px;
}

.cms-pedidos-filtros {
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin-bottom: 12px;
}

.cms-pedidos-filtros-row {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 10px;
}

.cms-pedidos-label {
	font-size: 13px;
	font-weight: 600;
}

.cms-pedidos-form {
	margin: 0;

	::v-deep .el-form-item {
		margin-bottom: 0;
	}
}

.cms-pedidos-resumen {
	display: flex;
	flex-wrap: wrap;
	gap: 16px;
	margin-bottom: 12px;
	font-size: 13px;
	color: #606266;
}

.cms-pedidos-body {
	min-height: 120px;
}

.cms-articulos-body {
	min-height: 120px;

	&--tall {
		min-height: 680px;
	}
}

.cms-articulos-layout {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.cms-articulos-section {
	h4 {
		margin: 0 0 4px;
		font-size: 14px;
		font-weight: 600;
	}

	&--col {
		min-width: 0;
	}
}

.cms-articulos-triple {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 12px;
	align-items: start;

	&--tall {
		align-items: stretch;
		min-height: 660px;

		.cms-articulos-section--col {
			display: flex;
			flex-direction: column;
			min-height: 640px;
		}

		.cms-table {
			flex: 1;
		}
	}
}

.cms-articulos-double {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 12px;
	align-items: start;
}

.cms-articulos-hint {
	margin: 0 0 10px;
	font-size: 12px;
	color: #909399;
}

.cms-articulos-hint-row {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	margin-bottom: 10px;

	.cms-articulos-hint {
		margin: 0;
		flex: 1;
	}
}

.cms-articulos-info {
	font-size: 16px;
	color: #409eff;
	cursor: help;
	margin-top: 2px;
}

.cms-articulos-filtros {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	margin-bottom: 10px;
}

.cms-articulos-filtro {
	width: 240px;
	max-width: 100%;
}

.cms-articulos-pagination {
	margin-top: 10px;
	text-align: right;
}

.cms-table {
	width: 100%;

	&--compact {
		margin-bottom: 0;
	}

	&--dense {
		::v-deep th,
		::v-deep td {
			padding: 8px 0;
		}

		::v-deep .cell {
			font-size: 12px;
			line-height: 1.35;
			padding-left: 6px;
			padding-right: 6px;
		}

		::v-deep th .cell {
			font-size: 12px;
		}

		::v-deep .cms-col-cod .cell {
			white-space: nowrap;
			font-variant-numeric: tabular-nums;
			letter-spacing: -0.02em;
		}
	}
}

@media (max-width: 1100px) {
	.cms-kpis {
		grid-template-columns: repeat(2, 1fr);
	}

	.cms-charts-grid,
	.cms-charts-grid--triple,
	.cms-buckets-grid,
	.cms-articulos-triple,
	.cms-articulos-double,
	.cms-ubicaciones-kpis {
		grid-template-columns: 1fr;
	}

	.cms-buckets {
		grid-template-columns: repeat(2, 1fr);
	}
}
</style>
